<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { required } from "../../../../utils/validation";
import {
  Item,
  ItemBatch,
  ItemSerial,
  RecordStatus,
  Warehouse,
  WarehouseShelf,
} from "../../../../types/models";
import {
  documentActions,
  downloadPDF,
  emailPDF,
  formatDate,
  isEmpty,
  printLabel,
  printPDF,
  sharePDF,
} from "../../../../utils/CommonFunctions";
import { tokenManager } from "../../../../api/utils/tokenManager";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import ItemGridSelectItems from "../../../../components/ui/Templates/ItemGridSelectItems.vue";
import GridInput from "../../../../components/ui/form/GridInput.vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";
import DateInput from "../../../../components/ui/form/DateInput.vue";
import Buttons from "../../../../components/ui/form/Buttons.vue";
import MessageBox from "../../../../services/MessageBox";
import AddListItemForm from "../../itemmanagement/variants/AddListItemForm.vue";
//import DropDownButton from "../../../../components/ui/common/DropdownButton.vue";
import ImportStockCountForm from "./ImportStockCountForm.vue";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  recordStatus?: RecordStatus[];
  cartItems?: Item[];
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save"): void;
  (e: "viewDocument", data: any, title: string, routePath: string): void;
}>();

const isReadOnly = ref<boolean>(props.mode === "view");

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  setFormData,
  resetForm,
} = useFormValidation({
  id: props.data?.id || "",
  outlets_id: props.data?.outlets_id || "",
  warehouses_id: props.data?.warehouses_id || "",
  date_time: props.data?.date_time || new Date().toISOString().split("T")[0],
  ref_no: props.data?.ref_no || "",
  attachment: props.data?.attachment || "",
  reason: props.data?.reason || "",
  fresh_count: props.data?.fresh_count || false,
  adjustment_account_id: props.data?.adjustment_account_id || "",
  remarks: props.data?.remarks || "",
  outlet: props.data?.outlet || "",
  price_lists_id: props.data?.price_lists_id || "",
  warehouse: props.data?.warehouse || "",
  uid: props.data?.uid || "",
  items: [] as any,
  download: false,
});

const warehouse = ref<Warehouse>();

//setFieldRules("outlets_id", [required("Select Outlet")]);
setFieldRules("warehouses_id", [required("Select Warehouse")]);
//setFieldRules("reason", [required("Enter reason")]);
setFieldRules("adjustment_account_id", [required("Select Adjustment Account")]);
//setFieldRules("items", [required("Select at least 1 item")]);

const getRecord = async () => {
  if (isEmpty(props.data)) return;
  const params = {
    id: props.data.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    setFormData(response.data);
  }
};

const handleSubmit = async (
  new_record: boolean,
  download: boolean = false,
  mail: boolean = false,
  share: boolean = false,
  print = false
) => {
  if (validateForm()) {
    formData.value.download = download || share;
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show("saved successfully");
      emit("save");
      if (new_record) {
        clearForm();
      } else if (download) {
        downloadPDF(response.data.file);
      } else if (mail) {
        emailPDF(props.endPoint, response.data.uid);
      } else if (share) {
        sharePDF(
          response.data.file,
          "Stock Request #: " + response.data.ref_no,
          "viber"
        );
      } else if (print) {
        printPDF(response.data.file);
      } else {
        setFormData(response.data);
      }
    }
  }
};

const clearForm = () => {
  resetForm();

  fields.value.date_time.value = formatDate(new Date());
  fields.value.warehouses_id.value = tokenManager.getSetting(
    "default_warehouse",
    true
  );
  isReadOnly.value = false;

  nextTick(() => {
    const input = document.getElementById("customers_id");
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

const showImportForm = ref(false)

const search = ref("");
const displayItems = ref<Item[] | []>([]);
const selectedItem = ref<Item | null>();
const showItemsDialog = ref(false);

const handleSearch = async () => {
  selectedItem.value = null;
  itemList.value = [];
  if (isEmpty(search.value)) return;
  const params = {
    search_query: search.value,
    warehouses_id: props.data?.warehouses_id,
    price_lists_id: formData.value.price_lists_id,
    outlets_id: formData.value.outlets_id,
    stock_counts_id: props.data?.uid,
  };
  const response = await api.post(
    "inventory/item-management/items/items",
    params
  );
  if (response.success == true) {
    if (response.data.length == 1 && !response.data[0].has_variants) {
      addItem(response.data[0]);
      handleGetVariants(response.data[0]);
    } else if (response.data.length > 0) {
      //if (warehouse.value?.shelf_managed) {
      //  displayItems.value = response.data;
      //  showItemsDialog.value = true;
      //} else {
        itemList.value = response.data;
        itemList.value.forEach((item) => {
          item.quantity = 0;
          item.shelfs = [];
          const ws: WarehouseShelf = {
            id: null,
            sales_invoice_items_id: null,
            warehouses_id: null,
            warehouse_shelfs_id: null,
            items_id: null,
            item_variants_id: null,
            quantity: 0,
            status_id: null,
            on_hand: null,
            code: null,
            name: null
          };
          item.shelfs?.push(ws)
        });
      //}
      handleGetVariants(response.data[0]);
      selectedItem.value = response.data[0];
      console.log(itemList.value, "itemList.value");
    } else {
      Toast.show("Item not found", "warning");
    }
  } else {
    Toast.show("Item not found", "warning");
  }
};

const searchInput = ref<HTMLInputElement | null>(null);
const focusSearch = () => {
  nextTick(() => {
    search.value = "";
    searchInput.value?.focus();
  });
};

const count_quntity = ref<HTMLInputElement | null>(null);
const focusCountQuantity = () => {
  nextTick(() => {
    count_quntity.value = document.getElementById(
      "count_quntity"
    ) as HTMLInputElement;
    if (selectedItem.value) {
      selectedItem.value.quantity = 0; // Assign a number instead of a string
    }
    count_quntity.value?.focus();
  });
};

const serialInput = ref<HTMLInputElement | null>(null);
const batchInput = ref<HTMLInputElement | null>(null);

const itemList = ref<Item[]>([]);
const expandedRows = ref<Set<string>>(new Set());
const activeTab = ref("shelfs");
const addItem = (item: Item) => {
  if (item) {
    item.warehouses_id = props.data?.warehouses_id;
    item.quantity = 0;
    if (
      item.tracked_by == "Batch" ||
      item.tracked_by == "Serial" ||
      warehouse.value?.shelf_managed
    ) {
      if (item.tracked_by == "Batch") {
        activeTab.value = "batch";

        if (item.shelfs) {
          item.batches?.forEach((b) => {
            b.shelfs = item.shelfs?.filter(
              (e: any) =>
                e.warehouses_id == props.data?.warehouses_id &&
                e.item_variants_id == item.item_variants_id
            );
          });
        }
      } else if (item.tracked_by == "Serial") {
        activeTab.value = "serial";
      } else if (item.shelfs && item.shelfs.length > 0) {
        activeTab.value = "shelfs";
        if (item.shelfs) {
          item.shelfs = item.shelfs.filter(
            (e: any) =>
              e.warehouses_id == props.data?.warehouses_id &&
              e.item_variants_id == item.item_variants_id
          );
        }
      }

      if (warehouse.value?.shelf_managed) {
        item.shelfs = warehouse.value?.shelfs?.filter(
          (e: any) => e.warehouses_id == props.data.warehouses_id
        );
      }

      expandedRows.value.add(String(item.id));
      console.log(expandedRows.value, "expandedRows.value");
    }

    console.log(item);
    selectedItem.value = item;
    showItemsDialog.value = false;
    focusCountQuantity();
  }
};

const handleSelectedItems = async (items: Item[]) => {
  items.forEach(async (item) => {
    let hasItem = itemList.value.find((e) => e.code == item.code);
    if (!hasItem) {
      addItem(item);
    }
  });
};

const handleUploadSuccess = async (file: any, item: any) => {
  console.log(item);
  console.log(file);
  if (item) {
    const response = await api.post(
      "clients/inventory/item-management/items/photo-update",
      {
        id: item.uid,
        item_variants_id: item.item_variants_id,
        photo: file,
      }
    );

    if (response.success == true) {
      if (itemList.value.length <= 0) {
      focusCountQuantity();
    }
    } else {
      if (itemList.value.length <= 0) {
      focusCountQuantity();
    }
    }
  }
};

const updateCount = async () => {
  if (
    (selectedItem.value && selectedItem.value.tracked_by == "Quantity") ||
    (selectedItem.value &&
      selectedItem.value.tracked_by == "Serial" &&
      selectedItem.value.quantity &&
      selectedItem.value.quantity > 0 &&
      selectedItem.value.serials?.length == selectedItem.value.quantity) ||
    (selectedItem.value &&
      selectedItem.value.tracked_by == "Batch" &&
      selectedItem.value.quantity &&
      selectedItem.value.quantity > 0 &&
      selectedItem.value?.batches?.reduce(
        (sum: number, batch: { quantity: number }) => {
          if (typeof batch.quantity === "number") {
            return sum + batch.quantity;
          }
          return sum;
        },
        0
      ) == selectedItem.value.quantity)
  ) {
    if (
      selectedItem.value.quantity == null ||
      selectedItem.value.quantity === undefined
    ) {
      Toast.show("Enter count quantity", "warning");
      return;
    }

    if (warehouse.value?.shelf_managed) {
      if (selectedItem.value.tracked_by == "Quantity") {
        const shelf_qty = selectedItem.value?.shelfs?.reduce(
          (sum: number, shelf: { quantity: number | null }) => {
            if (shelf.quantity !== null && typeof shelf.quantity === "number") {
              return sum + shelf.quantity;
            }
            return sum;
          },
          0
        );
        if (shelf_qty != selectedItem.value.quantity) {
          Toast.show("Shelf quantity not equal to count quantity", "warning");
          return;
        }
      } else if (selectedItem.value.tracked_by == "Batch") {
        const batch_qty = selectedItem.value?.batches?.reduce(
          (sum: number, batch: ItemBatch) => {
            if (
              typeof batch.quantity === "number" &&
              !isEmpty(batch.warehouse_shelfs_id)
            ) {
              return sum + batch.quantity;
            }
            return sum;
          },
          0
        );
        if (batch_qty != selectedItem.value.quantity) {
          Toast.show(
            "Shelf has to be Selected for all Batches with Quantity",
            "warning"
          );
          return;
        }
      } else if (selectedItem.value.tracked_by == "Serial") {
        const serial_qty = selectedItem.value?.serials?.reduce(
          (sum: number, serial: ItemSerial) => {
            if (
              typeof serial.quantity === "number" &&
              !isEmpty(serial.warehouse_shelfs_id)
            ) {
              return sum + serial.quantity;
            }
            return sum;
          },
          0
        );
        if (serial_qty != selectedItem.value.quantity) {
          Toast.show(
            "Shelf has to be Selected for all the Serial Nos",
            "warning"
          );
          return;
        }
      }
    }

    if (selectedItem.value.quantity == 0) {
      const confirmed = await MessageBox.confirm({
        title: "Count Zero",
        message:
          "The counted Quantity is Zero <br /> Are you sure you want to proceed?",
        confirmText: "Yes",
        cancelText: "No",
        type: "warning",
      });
      if (!confirmed) return;
    }
    const response = await api.post(props.endPoint + "count-single", {
      id: formData.value.uid,
      warehouses_id: props.data?.warehouses_id,
      adjustment_account_id: formData.value.adjustment_account_id,
      item: selectedItem.value,
    });
    if (response.success == true) {
      Toast.show("Count added successfully", "info");
      selectedItem.value = null;
      focusSearch();
    }
  } else if (selectedItem.value && selectedItem.value.tracked_by == "Serial") {
    serialInput.value = document.getElementById(
      "serialInput"
    ) as HTMLInputElement;
    serialInput.value?.focus();
  } else if (selectedItem.value && selectedItem.value.tracked_by == "Batch") {
    batchInput.value = document.getElementById(
      "batchInput"
    ) as HTMLInputElement;
    batchInput.value?.focus();
  }
};

const handleValidateAllCounts = () => {
  if (!warehouse.value?.shelf_managed)
    return true;
  let valid = true;
  itemList.value.forEach(element => {
    const shelf_qty = element?.shelfs?.reduce(
      (sum: number, shelf: { quantity: number | null; warehouse_shelfs_id: any; }) => {
        if (shelf.quantity !== null && typeof shelf.quantity === "number" && shelf?.warehouse_shelfs_id) {
          return sum + shelf.quantity;
        }
        return sum;
      },
      0
    );
    if (shelf_qty != element.quantity) {
      valid = false;
      Toast.show("Shelf quantity not equal to count quantity", "warning");
      element.error = "Shelf Quantity not equal to Count Quantity";
    }
  });

  console.log(itemList.value, "itemList.value");
  return valid;
}

const updateAllCount = async () => {
  if (!handleValidateAllCounts()) return;
  const response = await api.post(props.endPoint + "count", {
    id: formData.value.uid,
    warehouses_id: props.data?.warehouses_id,
    adjustment_account_id: formData.value.adjustment_account_id,
    items: itemList.value,
  });
  if (response.success == true) {
    Toast.show("Count added successfully", "info");
    itemList.value = [];
    focusSearch();
  }
};

const serial_no = ref("");
const addSerial = () => {
  if (serial_no.value && serial_no.value.length > 1) {
    if (selectedItem.value) {
      selectedItem.value?.serials?.push({
        serial_no: serial_no.value,
        quantity: 1,
      });
      serial_no.value = "";
      if (selectedItem.value?.serials?.length == selectedItem.value?.quantity) {
        updateCount();
      }
    } else {
      Toast.show("Enter Serial No", "warning");
    }
  }
};

const batchQtyInput = ref<HTMLInputElement | null>(null);
const focusBatchQtyInput = () => {
  nextTick(() => {
    batchQtyInput.value = document.getElementById(
      "batchQtyInput"
    ) as HTMLInputElement;
    console.log("focusBatchQtyInput", batchQtyInput.value);
    batchQtyInput.value?.focus();
  });
};

const batch_no = ref("");
const batch_qty = ref("");
const addBatch = () => {
  if (batch_no.value && batch_no.value.length > 1) {
    if (selectedItem.value) {
      let total_qty = selectedItem.value?.batches?.reduce(
        (sum: number, batch: { quantity: number }) => {
          if (typeof batch.quantity === "number") {
            return sum + batch.quantity;
          }
          return sum;
        },
        0
      );
      console.log(total_qty, "total_qty");
      if (
        selectedItem.value?.quantity - (total_qty || 0) <
        Number(batch_qty.value)
      ) {
        Toast.show("Quantity exceeds the Counted quantity", "warning");
        return;
      }
      selectedItem.value?.batches?.push({
        batch_no: batch_no.value,
        quantity: Number(batch_qty.value),
      });
      batch_no.value = "";
      total_qty = selectedItem.value?.batches?.reduce(
        (sum: number, batch: { quantity: number }) => {
          if (typeof batch.quantity === "number") {
            return sum + batch.quantity;
          }
          return sum;
        },
        0
      );

      console.log(
        total_qty,
        "total_qty",
        selectedItem.value?.quantity,
        "selectedItem.value?.quantity"
      );
      if (total_qty == selectedItem.value?.quantity) {
        updateCount();
      } else {
        batch_qty.value = "";
        batchInput.value = document.getElementById(
          "batchInput"
        ) as HTMLInputElement;
        batchInput.value?.focus();
      }
    } else {
      Toast.show("Enter Batch No Quantity", "warning");
    }
  }
};

const shelfs = ref([])

const getWarehouse = async () => {
  const response = await api.post("/clients/system/company/warehouses/list", {
    id: props.data.warehouses_id,
  });
  if (response.success == true) {
    warehouse.value = response.data;
    shelfs.value = response.data.shelfs;
    console.log(warehouse.value, "warehouse.value");
  }
};

const handleRemovePhoto = async (item: Item) => {
  console.log(item, "item");
  if (item) {
    const response = await api.post(
      "/clients/inventory/item-management/items/remove-photo",
      {
        id: item.uid,
      }
    );
    if (response.success == true) {
      Toast.show("Photo removed successfully", "info");
      focusCountQuantity();
    }
  }
};

const handleActions = async (action: string) => {
  documentActions(props.endPoint, formData.value.uid, action, "Add Count# ");
};

const showForm = ref(false);
onMounted(() => {
  showForm.value = true;
  isReadOnly.value = props.mode == "view";
  if (props.mode != "create") {
    getRecord();
  } else {
    clearForm();
    getWarehouse();
  }
  count_quntity.value = document.getElementById(
    "count_quntity"
  ) as HTMLInputElement;
});

const showVariantForm = ref(false);

const handleVariantListAssigned = async (data: any, photo: string) => {
  if (data) {
    const response = await api.post(
      "/clients/inventory/item-management/items/variant-create",
      {
        items_id: selectedItem.value?.id,
        variants_id: data.variants_id,
        variant_lists_id: data.id,
        code: data.code,
        photo: photo,
        name: data.name,
      }
    );
    if (response.success == true) {
      showVariantForm.value = false;
      handleSearch()
    }
  }
};

const handleVariantListAdded = async () => {
  showVariantForm.value = false;
  handleSearch()
};

const variantList = ref<any[]>([]);
const handleGetVariants = async (item: any) => {
  if (item && item.variants) {
    const response = await api.post(
      "/clients/inventory/item-management/variants/list-get",
      {
        variants_id: item.variants,
      }
    );
    if (response.success == true) {
      variantList.value = response.data.filter(
        (e: any) =>
          !itemList.value.some((item: any) => item.variant_lists_id === e.id)
      );
    }
  }
};

const selectedVariantItem = ref<any | null>(null);
const variantFormMode = ref<"create" | "edit" | "view">("create");
const handleSelectAssignVariant = (item: any) => {
  if (!item) return;
  const variant = variantList.value.find((e) => e.id == item.id);
  console.log(variant, "variant");
  if (selectedItem.value) {
    variantFormMode.value = "view";
    selectedVariantItem.value = {
      id: variant.id,
      name: variant.name,
      items_id: selectedItem.value.id,
      variants_id: variant.variants_id,
      item_variants_id: selectedItem.value.id,
      code: variant.code,
    };
    console.log(selectedVariantItem.value, "selectedVariantItem.value");
    showVariantForm.value = true;
  } else {
    Toast.show("Select Item", "warning");
  }
};

/*const handleAssignVariant = (variant: any) => {
  console.log(variant, "variant");
  if (selectedItem.value) {
    variantFormMode.value = "view";
    selectedVariantItem.value = {
      id: variant.id,
      name: variant.name,
      items_id: selectedItem.value.id,
      variants_id: variant.variants_id,
      item_variants_id: selectedItem.value.id,
      code: variant.code,
    };
    console.log(selectedVariantItem.value, "selectedVariantItem.value");
    showVariantForm.value = true;
  } else {
    Toast.show("Select Item", "warning");
  }
};*/

const item_variants_id = ref(null)

const handleNewVariant = () => {
  if (selectedItem.value) {
    variantFormMode.value = "create";
    selectedVariantItem.value = null;
    showVariantForm.value = true;
  } else {
    Toast.show("Select Item", "warning");
  }
};

const printPriceLabel = (item: Item) => {
  if (item && item.code && item.name) {
    printLabel(
      item?.code,
      item?.name,
      String(item?.selling_price),
      formData.value.outlets_id || ""
    );
  } else {
    Toast.show("Some details missing", "warning");
  }
};
</script>

<template>
  <ModalFormComponent
    :width="mode == 'view' ? '100%' : '50rem'"
    @close="$emit('close')"
    :fullscreen="mode == 'view'"
  >
    <template #header>
      <!-- Header -->
      <DocumentHeader
        :title="title"
        :end-point="endPoint"
        :data="formData"
        :readonly="isReadOnly"
        :show-drop-down="isReadOnly"
        @close="$emit('close')"
        :permissions="permissions"
        @clear="clearForm"
        @new="clearForm"
        @save="handleSubmit(false)"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="false"
        :show-save-icon="false"
      >
        <template #dropdownbuttons> </template>
        <template #write-buttons>
          <Buttons icon="Import" caption="Import from Excel" @click="showImportForm = true" />
        </template>
      </DocumentHeader>
    </template>
    <!-- Main Content - Scrollable -->
    <template #form>
      <!--header-->
      <div class="grid grid-cols-5 gap-6 mb-8">
        <DateInput
          v-model="fields.date_time.value"
          label="Date"
          :readonly="true"
          :error="fields.date_time.touched ? fields.date_time.error : null"
          @blur="fields.date_time.touched = true"
        />
        <TextInput
          v-model="fields.ref_no.value"
          label="Reference No"
          :readonly="true"
          :error="fields.ref_no.touched ? fields.ref_no.error : null"
          @blur="fields.ref_no.touched = true"
        />

        <TextInput
          v-model="fields.outlet.value"
          v-if="fields.outlet.value != ''"
          class="col-span-2"
          label="Outlet"
          :readonly="true"
          :error="fields.outlet.touched ? fields.outlet.error : null"
          @blur="fields.outlet.touched = true"
        />

        <TextInput
          v-model="fields.warehouse.value"
          label="Warehouse"
          class="col-span-2"
          :readonly="true"
          :error="fields.warehouse.touched ? fields.warehouse.error : null"
          @blur="fields.warehouse.touched = true"
        />

        <TextInput
          v-model="fields.remarks.value"
          label="Remarks"
          v-if="fields.remarks.value != ''"
          class="col-span-2"
          :readonly="true"
          :error="fields.remarks.touched ? fields.remarks.error : null"
          @blur="fields.remarks.touched = true"
        />
      </div>
      <div>
        <div>
          <div class="flex-1 max-w-md relative" id="table-search">
            <IconComponent
              name="Search"
              class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              v-model="search"
              ref="searchInput"
              @keyup.enter="handleSearch"
              placeholder="Search..."
              class="w-full pl-10 pr-10 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
            <button
              v-if="search"
              @click="search = ''"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
            >
              <IconComponent name="Trash2" class="w-4 h-4" />
            </button>
          </div>
        </div>
        <div class="bg-gray-100 dark:bg-gray-900 rounded-lg shadow-md mt-4 px-2 py-2" v-if="
              selectedItem?.has_variants ||
              !isEmpty(selectedItem?.item_variants_id)
            ">
          <div
            class="flex items-center justify-start mt-4"
            
          >
            <Buttons
              icon="Shapes"
              caption="Add Variant"
              variant="primary"
              class="px-4 py-4"
              @click="handleNewVariant"
            />
            <SearchSelect @select="handleSelectAssignVariant" v-if="variantList.length > 0" v-model="item_variants_id" :options="variantList" label="" placeholder="Select Variant" class="ml-2" />
            <!--<DropDownButton
              key="action-dropdown-sales"
              id="actions-dropdown-sales"
              text="Assign Variant"
              class="ml-2"
              icon="Shapes"
              v-if="variantList.length > 0"
            >
              <Buttons
                v-if="selectedItem?.has_variants"
                v-for="(item, index) in variantList"
                :key="index"
                class="w-full px-1 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                :caption="item.name"
                @click="handleAssignVariant(item)"
              />
            </DropDownButton>-->
          </div>
        </div>
      </div>
      <!--Single Item-->
      <div v-if="selectedItem && (!selectedItem.item_variants_id || !itemList)" class="mt-4">
        <div class="flex items-center space-x-4">
          <div>
            <FileUpload
              label="Photo"
              accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
              :max-size="5"
              :multiple="false"
              :readonly="false"
              :return="true"
              :return-item="selectedItem"
              @remove="handleRemovePhoto(selectedItem)"
              @upload-success="handleUploadSuccess"
              :image="true"
              path="items"
              v-model="selectedItem.photo"
            />
            <Buttons
              caption="Print Label"
              icon="Newspaper"
              type="info"
              class="mt-2"
              @click="printPriceLabel(selectedItem)"
              v-if="selectedItem && !selectedItem?.has_variants"
            />
          </div>
          <div class="flex flex-col space-y-2 pb-5">
            <div><strong>Code:</strong> {{ selectedItem?.code }}</div>
            <div><strong>Name:</strong> {{ selectedItem?.name }}</div>
            <div><strong>On Hand:</strong> {{ selectedItem?.on_hand }} <span class="bg-gray-300 dark:bg-gray-900 rounded-sm shadow px-1 py-2">Counted: {{ selectedItem?.counted }}</span></div>
            <div v-if="selectedItem?.selling_price">
              <strong>Price:</strong> {{ selectedItem?.selling_price }}
            </div>
            <div
              class="flex items-center space-x-4"
              v-if="!selectedItem?.has_variants || !selectedItem.item_variants_id || !itemList || itemList.length <= 1"
            >
              <div>
                <strong>Couint Quantity:</strong>
              </div>
              <div class="max-w-[200px]">
                <NumberInput
                  v-model="selectedItem.quantity"
                  name="count_quntity"
                  @keyup.enter="updateCount"
                />
              </div>
            </div>
            <Buttons
              icon="Plus"
              v-if="!selectedItem?.has_variants || !selectedItem.item_variants_id || !itemList || itemList.length <= 1"
              caption="Add Count"
              variant="primary"
              class="ml-auto w-full px-4 py-4"
              @click="updateCount"
            />
            <!--<div>
                    <strong>Cost:</strong> {{ selectedItem?.cost }}
                </div>
                <div>
                    <strong>Price:</strong> {{ selectedItem?.price }}
                </div>-->
          </div>
        </div>
        <div v-if="selectedItem?.tracked_by == 'Serial'">
          <GridInput label="Serials">
            <div class="flex items-center space-x-4">
              <TextInput
                v-model="serial_no"
                label="Serial No"
                ref="serialInput"
                name="serialInput"
                id="serialInput"
                @keyup.enter="addSerial"
                class="flex-1"
              />
              <Buttons
                icon="Plus"
                caption="Add Serial"
                variant="primary"
                class="px-4 py-2 mt-6"
                @click="addSerial"
              />
            </div>
            <table class="mt-8">
              <thead>
                <tr
                  class="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                >
                  <th class="w-[250px] text-left">
                    Serial No ({{ selectedItem?.serials?.length }})
                  </th>
                  <th v-if="warehouse?.shelf_managed">Shelf</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(serial, index) in selectedItem?.serials"
                  :key="index"
                  class="border-b dark:border-gray-700"
                >
                  <td class="py-2 px-4 text-gray-800 dark:text-gray-200">
                    {{ serial.serial_no }}
                  </td>
                  <td
                    class="py-2 px-4 text-gray-800 dark:text-gray-200"
                    v-if="warehouse?.shelf_managed"
                  >
                    <SearchSelect
                      v-model="serial.warehouse_shelfs_id"
                      :options="
                        selectedItem?.shelfs?.map((shelf) => ({
                          ...shelf,
                          name: shelf.name || '',
                        }))
                      "
                      label="Shelf"
                      placeholder="Select Shelf"
                    />
                  </td>
                  <td class="py-2 px-4 text-gray-800 dark:text-gray-200">
                    <IconComponent
                      name="Trash2"
                      class="w-6 h-6 cursor-pointer"
                      @click="selectedItem?.serials?.splice(index, 1)"
                    />
                  </td>
                </tr>
                <tr
                  v-if="!selectedItem?.serials"
                  class="border-b dark:border-gray-700"
                >
                  <td
                    class="py-2 px-4 text-gray-800 dark:text-gray-200 col-span-2"
                  >
                    There are no serials ...
                  </td>
                </tr>
              </tbody>
            </table>
          </GridInput>
        </div>

        <div v-else-if="selectedItem?.tracked_by == 'Batch'">
          <GridInput label="Batches">
            <div class="flex items-center space-x-4">
              <TextInput
                v-model="batch_no"
                label="Batch No"
                ref="batchInput"
                name="batchInput"
                id="batchInput"
                @keyup.enter="focusBatchQtyInput"
                class="flex-1"
              />
              <NumberInput
                v-model="batch_qty"
                label="Batch Qty"
                name="batchQtyInput"
                @keyup.enter="addBatch"
                class="flex-1"
              />
              <Buttons
                icon="Plus"
                caption="Add Batch"
                variant="primary"
                class="px-4 py-2 mt-6"
                @click="addBatch"
              />
            </div>
            <table class="mt-8">
              <thead>
                <tr
                  class="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                >
                  <th class="w-[250px] text-left">
                    Batches ({{ selectedItem?.batches?.length }})
                  </th>
                  <th v-if="warehouse?.shelf_managed">Shelf</th>
                  <th class="text-right">Quantity</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(batch, index) in selectedItem?.batches"
                  :key="index"
                  class="border-b dark:border-gray-700"
                >
                  <td class="py-2 px-4 text-gray-800 dark:text-gray-200">
                    {{ batch.batch_no }}
                  </td>
                  <td
                    class="py-2 px-4 text-gray-800 dark:text-gray-200"
                    v-if="warehouse?.shelf_managed"
                  >
                    <SearchSelect
                      v-model="batch.warehouse_shelfs_id"
                      :options="
                        selectedItem?.shelfs?.map((shelf) => ({
                          ...shelf,
                          name: shelf.name || '',
                        }))
                      "
                      label="Shelf"
                      placeholder="Select Shelf"
                    />
                  </td>
                  <td
                    class="py-2 px-4 text-gray-800 dark:text-gray-200 text-right"
                  >
                    {{ batch.quantity }}
                  </td>
                  <td class="py-2 px-4 text-gray-800 dark:text-gray-200">
                    <IconComponent
                      name="Trash2"
                      class="w-6 h-6 cursor-pointer"
                      @click="selectedItem?.batches?.splice(index, 1)"
                    />
                  </td>
                </tr>
                <tr
                  v-if="!selectedItem?.batches"
                  class="border-b dark:border-gray-700"
                >
                  <td
                    class="py-2 px-4 text-gray-800 dark:text-gray-200 col-span-2"
                  >
                    There are no Batches ...
                  </td>
                </tr>
              </tbody>
            </table>
          </GridInput>
        </div>

        <div v-else-if="warehouse?.shelf_managed">
          <GridInput label="Shelfs">
            <table class="mt-8">
              <thead>
                <tr
                  class="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                >
                  <th class="w-[250px] text-left">Code</th>
                  <th class="w-[250px] text-left">Name</th>
                  <th class="text-right">Quantity</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(shelf, index) in selectedItem?.shelfs"
                  :key="index"
                  class="border-b dark:border-gray-700"
                >
                  <td class="py-2 px-4 text-gray-800 dark:text-gray-200">
                    {{ shelf.code }}
                  </td>
                  <td class="py-2 px-4 text-gray-800 dark:text-gray-200">
                    {{ shelf.name }}
                  </td>
                  <td
                    class="py-2 px-4 text-gray-800 dark:text-gray-200 text-right"
                  >
                    <NumberInput
                      v-model="shelf.quantity"
                      name="shelf_qty"
                      :show-controls="false"
                      label=""
                      class="w-[100px]"
                    />
                  </td>
                </tr>
                <tr
                  v-if="!selectedItem?.batches"
                  class="border-b dark:border-gray-700"
                >
                  <td
                    class="py-2 px-4 text-gray-800 dark:text-gray-200 col-span-2"
                  >
                    There are no Shelfs ...
                  </td>
                </tr>
              </tbody>
            </table>
          </GridInput>
        </div>
      </div>
      <!-- variants -->
      <div v-if="itemList && itemList.length > 0" class="mt-4">
        <div
          class="flex items-center space-x-4 mt-4 mb-4 border-b shadow py-3"
          v-for="item in itemList"
        >
          <div class="min-w-[300px] w-[300px]">
            <FileUpload
              label="Photo"
              accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
              :max-size="5"
              @remove="handleRemovePhoto(item)"
              @upload-success="handleUploadSuccess"
              :return-item="item"
              :multiple="false"
              :readonly="false"
              :image="true"
              path="items"
              v-model="item.photo"
            />
          </div>
          <div class="flex flex-col space-y-2">
            <div><strong>Code:</strong> {{ item?.code }}</div>
            <div><strong>Name:</strong> {{ item?.name }}</div>
            <div><strong>On Hand:</strong> {{ item?.on_hand }} <span class="bg-gray-300 dark:bg-gray-900 rounded-sm shadow px-1 py-2">Counted: {{ item?.counted }}</span></div>
            <div class="flex items-center space-x-4"><strong>Update Price & Cost:</strong> <CheckInput label="" v-model="item.update_price" /></div>
            <div class="flex items-center space-x-4"><strong>Price:</strong> <NumberInput v-model="item.selling_price" /></div>
            <div class="flex items-center space-x-4"><strong>Cost:</strong> <NumberInput v-model="item.cost" /></div>
            <div class="flex items-center space-x-4">
              <div>
                <strong>Count Quantity:</strong>
              </div>
              <div class="max-w-[200px]" :class="{
          ' border rounded-md shadow-sm border-red-300 dark:border-red-700': item.error,
          'border-gray-300 dark:border-gray-600': !item.error
        }">
                <NumberInput v-model="item.quantity" name="count_quntity" />
                <p v-if="item.error" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ item.error }}</p>
              </div>
            </div>
            <div class="flex items-center space-x-4" v-if="warehouse?.shelf_managed" v-for="shelf in item.shelfs">
              <div>
                <SearchSelect
                      v-model="shelf.warehouse_shelfs_id"
                      :options="shelfs"
                      label="Shelf"
                      placeholder="Select Shelf"
                    />
              </div>
              <div class="max-w-[200px]">
                <NumberInput v-model="shelf.quantity" label="Quantity" name="count_quntity" />
              </div>
            </div>
            <div>
              <Buttons
                caption="Print Label"
                icon="Newspaper"
                type="info"
                class="mt-2"
                @click="printPriceLabel(item)"
              />
            </div>
          </div>
        </div>
        <div>
          <Buttons
            icon="Plus"
            caption="Add Counts"
            variant="primary"
            class="ml-auto w-full px-4 py-4"
            @click="updateAllCount"
          />
        </div>
      </div>
      <ItemGridSelectItems
        v-if="showItemsDialog"
        :items="displayItems"
        :show-quantity-input="false"
        :warehouses_id="props.data?.warehouses_id"
        @close="showItemsDialog = false"
        @select-many="handleSelectedItems"
        @select="addItem"
      />
      <AddListItemForm
        title="Variant List"
        :permissions="[]"
        :variant="{
          id: selectedItem?.variants,
          name: selectedItem?.variant_names,
          items_id: selectedItem?.id,
        }"
        :data="selectedVariantItem"
        endPoint="/clients/inventory/item-management/variants/"
        :mode="variantFormMode"
        @close="showVariantForm = false"
        @assign="handleVariantListAssigned"
        @save="handleVariantListAdded"
        v-if="showVariantForm"
      />
    </template>
    <!-- Footer -->
    <template #footer>
      <DocumentFooter
        :data="formData"
        :readonly="isReadOnly"
        class="mt-6"
        :end-point="endPoint"
        :permissions="permissions"
        :title="title"
        convert-module="Debit Note"
        @close="$emit('close')"
        @save="handleSubmit(false)"
        @save-new="handleSubmit(true)"
        @download="handleSubmit(false, true)"
        @new="clearForm"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="false"
        :show-save-icon="false"
      />
    </template>
  </ModalFormComponent>
  <ImportStockCountForm
  v-if="showImportForm"
      :end-point="endPoint"
      :count="data"
      @close="showImportForm = !showImportForm" />
</template>
